/**
 * v3.99bs — VaultArchiveStrip
 *
 * A restrained, registry-feeling strip that surfaces the most recent
 * listings spawned from the CannaForge House Collection (admin's
 * personal seed library). Renders nothing if there are no vault
 * listings live — never ships a hollow band.
 *
 * Visual language: archival, not retail. Small thumbnails, breeder
 * label, "From the Vault" mark, single quiet link into /the-vault. No
 * countdown timers, no "add to cart" CTAs, no big imagery — this is a
 * scarcity / collector-checking signal, not a flash-sale carousel.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warehouse, ChevronRight } from "lucide-react";
import { api } from "../lib/apiClient";
import { listingUrl } from "../lib/urls";

export default function VaultArchiveStrip({ limit = 4 }) {
  const [items, setItems] = useState(null); // null=loading, []=empty, [...]=loaded

  useEffect(() => {
    let cancelled = false;
    api.get("/listings", { params: { from_house_collection: true, limit } })
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [limit]);

  // Loading + empty states both hide the strip — keeps the homepage
  // calm when the admin hasn't surfaced anything yet.
  if (!items || items.length === 0) return null;

  return (
    <section className="border-y border-[var(--border)] bg-[var(--surface)]"
             data-testid="vault-archive-strip">
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1.5"
                 style={{ color: "var(--gold)" }}>
              <Warehouse className="w-3 h-3" /> From the Vault
            </div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tighter"
                style={{ color: "var(--text)" }}>
              Surfaced from the archive.
            </h2>
            <p className="text-xs mt-1 max-w-xl" style={{ color: "var(--text-muted)" }}>
              Single-stock packs from the CannaForge house collection.
              No restocks. Listed quietly. Listed once.
            </p>
          </div>
          <Link to="/the-vault"
                className="text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
                style={{ color: "var(--gold)" }}
                data-testid="vault-strip-see-all">
            See the full archive <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((l) => {
            const img = (l.images && l.images[0]) || null;
            const breederLabel = l.listing_breeder_label || l.store_name;
            return (
              <Link key={l.id} to={listingUrl(l)}
                    className="group border border-[var(--border)] bg-[var(--bg)] hover:border-[var(--gold)]/60 transition-colors flex flex-col"
                    data-testid={`vault-strip-${l.id}`}>
                {img ? (
                  <div className="relative aspect-[4/3] overflow-hidden bg-black">
                    <img src={img} alt={l.title || l.strain_name}
                         className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                         style={{ filter: "saturate(0.85)" }}
                         loading="lazy"/>
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em] border backdrop-blur-sm inline-flex items-center gap-1"
                         style={{
                           borderColor: "rgba(192,161,95,0.55)",
                           color: "#C0A15F",
                           background: "rgba(11,11,11,0.6)",
                         }}>
                      <Warehouse className="w-2.5 h-2.5" /> Vault
                    </div>
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[var(--surface-2)] flex items-center justify-center">
                    <Warehouse className="w-6 h-6 opacity-30" style={{ color: "var(--gold)" }}/>
                  </div>
                )}
                <div className="p-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] truncate"
                       style={{ color: "var(--text-muted)" }}>
                    {breederLabel || "—"}
                  </div>
                  <div className="text-sm font-semibold mt-1 line-clamp-2"
                       style={{ color: "var(--text)" }}>
                    {l.title || l.strain_name}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
